import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
} from "@headlessui/react";
import { SelectorIcon, XIcon } from "@heroicons/react/solid";
import { Info, LoadingIndicator, RadioCheckedIcon, Shimmer, ShimmerType } from '@Wonder-Cave/ui';
import { FocusEventHandler, useState } from "react";
import { ErrorMessage } from "./ErrorMessage";

interface IDropdownCommonProps {
  error?: string;
  top?: boolean;
  showError?: boolean;
  label?: string;
  labelInfo?: string;
  disabled?: boolean;
  loading?: boolean;
  shimmer?: boolean;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  options?: IDropdownValue[];
  placeholder?: string;
  debounceTime?: number;
  full?: boolean;
  search?: boolean;
  labelColor?: string;
  readOnly?: boolean;
  leadingLabel?: boolean;
  gray?: boolean;
  errorLocation?: "BOTTOM";
  optionLabelClassName?: string;
  renderError?: boolean;
  onChange: (selectedValue: any) => void;
}

type IDropdownValueProps =
  | {
    multiple?: false;
    selectAll?: false;
    value?: IDropdownValue | undefined;
  }
  | {
    multiple?: true;
    selectAll?: boolean;
    value?: (IDropdownValue | undefined)[];
  };

export interface IDropdownValue {
  label: string;
  value: any;
  additionalData?: any;
}

type IDropdownProps = IDropdownCommonProps & IDropdownValueProps;

const OldDropdown = ({
  options,
  value: selectedOption,
  label,
  labelInfo,
  disabled,
  shimmer,
  loading,
  onChange,
  placeholder,
  error,
  showError,
  onBlur,
  readOnly,
  multiple = false,
  selectAll = false,
  top = false,
  search = true,
  full,
  labelColor,
  leadingLabel,
  gray,
  errorLocation,
  optionLabelClassName,
  renderError = true,
}: IDropdownProps): JSX.Element => {
  const [query, setQuery] = useState("");
  const removeOption = (id: string) => {
    const change = onChange as (
      selectedValue: IDropdownValue[] | undefined
    ) => void;
    change(
      (selectedOption as IDropdownValue[])?.filter(
        (option) => option.value !== id
      )
    );
  };

  const filteredOptions = options!
    .filter((option) =>
      Array.isArray(selectedOption)
        ? !selectedOption.some(
          (selectedOption) => selectedOption?.value === option?.value
        )
        : true
    )
    .filter((opt) => opt.label.toLowerCase().includes(query.toLowerCase()));

  if (selectAll) {
    filteredOptions.push({
      label: "Select All",
      value: "select-all",
    } as IDropdownValue);
  }

  return shimmer ? (
    <div className="w-full">
      <Shimmer type={ShimmerType.INPUT} />
    </div>
  ) : (
    <div>
      <div
        onBlur={onBlur}
        className={`${full ? "w-full" : ""} ${!label || leadingLabel ? "flex items-center" : ""
          } ${errorLocation === "BOTTOM" ? "flex-col" : ""}`}
      >
        <>
          <Combobox
            value={selectedOption}
            virtual={{ options: filteredOptions }}
            onChange={(value) => {
              if (multiple) {
                setQuery("");
              }
              onChange(value);
            }}
            multiple={multiple}
            disabled={disabled}
          >
            <>
              {!!label && (
                <Label
                  className={`${leadingLabel ? "mr-4" : "mb-5"} ${labelColor ?? "text-gray-700"
                    } ${labelInfo ? "flex" : ""}`}
                >
                  <span>{label}</span>
                  {!!labelInfo && (
                    <Info className="items-center ml-2" text={labelInfo} />
                  )}
                </Label>
              )}
              <div
                className={`relative w-full rounded-md ${multiple ? "border pl-1 py-1 border-gray-300 focus:ring-sky-500 focus:border-sky-500" : ""} ${gray || disabled ? "bg-light-gray" : "bg-white"
                  }`
                }
                style={{
                  height: "auto",
                }}
              >
                <div
                  className={`${!search && "cursor-pointer"
                    } flex relative flex-wrap gap-2 w-full text-left items-center`}
                >
                  {multiple &&
                    (selectedOption as IDropdownValue[])?.length > 0 && (
                      <div className="z-10 flex flex-wrap gap-2 pb-2 -mb-2">
                        {(selectedOption as IDropdownValue[]).map((option) => {
                          return (
                            <span
                              key={option?.value}
                              className={`flex items-center gap-1 rounded ${disabled ? "border border-slate-100" : ""
                                } bg-blue-50 pr-1 pl-2 py-0.375`}
                            >
                              <span className={optionLabelClassName ?? ""}>
                                {option?.label}
                              </span>
                              {!disabled && (
                                <div
                                  onClick={() => {
                                    removeOption(option?.value);
                                  }}
                                  className="w-4 h-4 cursor-pointer"
                                >
                                  <XIcon />
                                </div>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  <ComboboxInput
                    className={`${!search && "cursor-pointer"
                      } ${disabled ? "bg-light-gray" : "bg-white"} focus:shadow-none hover:shadow-none active:shadow-none focus-visible:outline-none body-text grow ${!multiple ? "border-gray-300 focus:ring-sky-500 focus:border-sky-500 shadow-sm" : "border-none py-1 focus:ring-0"} rounded-md`}
                    displayValue={(option: IDropdownValue) => {
                      if (!multiple) {
                        // Headless doesn't clear the input when the value is undefined
                        return !!selectedOption ? option?.label : '';
                      } else {
                        return query;
                      }
                    }}
                    onChange={(event: any) => {
                      setQuery(event.target.value);
                    }}
                    placeholder={disabled ? "" : (placeholder ?? "Select an option")}
                    onBlur={(event: any) => {
                      if (event.target.value === "" && !multiple) {
                        onChange(undefined);
                      }
                    }}
                    autoComplete={"off"}
                    readOnly={loading || !search || readOnly || disabled}
                    disabled={!search || disabled}
                  />
                </div>
                {loading ? (
                  <div className="absolute inset-y-0 right-0 flex items-center pl-2 pr-4">
                    <LoadingIndicator />
                  </div>
                ) : (
                  <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pl-2 pr-4">
                    <SelectorIcon />
                  </ComboboxButton>
                )}
                {(options?.length ?? 0) > 0 && (
                  <ComboboxOptions
                    className={`absolute z-20 w-full py-1 mt-1 overflow-auto top-full left-0 ${top && "mb-1 bottom-full"
                      } text-base ${gray ? "bg-light-gray" : "bg-white"
                      } rounded-md shadow-lg max-h-60 ring-black ring-opacity-5 ring-1 focus:outline-none sm:text-sm`}
                  >
                    {({ option }) => (
                      <ComboboxOption
                        key={option.value}
                        className={({ selected, focus }) =>
                          `cursor-pointer select-none relative py-2 pl-3 pr-9 w-full ${focus
                            ? "text-white dark:bg-slate-800 bg-sky-600"
                            : "text-gray-900 dark:text-slate-400"
                          }`
                        }
                        value={option}
                      >
                        {({ selected, focus }) => (
                          <>
                            <span
                              className={`block truncate`}
                            >
                              {option.label}
                            </span>

                            {selected ? (
                              <span
                                className={`absolute inset-y-0 right-0 flex items-center pr-4 ${focus ? "text-white" : "text-sky-600"
                                  }`}
                              >
                                <RadioCheckedIcon />
                              </span>
                            ) : null}
                          </>
                        )}
                      </ComboboxOption>
                    )}
                  </ComboboxOptions>
                )}
              </div>
            </>
          </Combobox>
        </>
      </div>
      {renderError && (
        <ErrorMessage show={showError ?? false} message={error} />
      )}
    </div>
  );
};

export default OldDropdown;
